import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, DataTable, RiskBadge, StatusBadge, TextInput, Select, formatCurrency, formatDate } from "@odingard/design-system";
import { EmptyState, LoadingState } from "@odingard/design-system";
import type { ContractSummary } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

export function CaiRepository() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const navigate = useNavigate();
  const state = useAsync(() => api.contracts(orgId), [orgId]);
  const [q, setQ] = useState("");
  const [risk, setRisk] = useState("");
  const [status, setStatus] = useState("");

  const rows = useMemo(() => {
    const all = state.data?.contracts ?? [];
    return all.filter(
      (c) =>
        (!q || `${c.title} ${c.counterparty}`.toLowerCase().includes(q.toLowerCase())) &&
        (!risk || c.riskLevel === risk) &&
        (!status || c.status === status),
    );
  }, [state.data, q, risk, status]);

  const cols = [
    { key: "title", header: "Contract", render: (c: ContractSummary) => <strong>{c.title}</strong> },
    { key: "cp", header: "Counterparty", render: (c: ContractSummary) => c.counterparty },
    { key: "type", header: "Type", render: (c: ContractSummary) => c.type },
    { key: "value", header: "Value", render: (c: ContractSummary) => (c.valueAmount ? formatCurrency(c.valueAmount, c.currency) : "Not stated") },
    { key: "renewal", header: "Renewal", render: (c: ContractSummary) => (c.renewalDate ? formatDate(c.renewalDate) : "Not set") },
    { key: "risk", header: "Risk", render: (c: ContractSummary) => <RiskBadge level={c.riskLevel} /> },
    { key: "status", header: "Status", render: (c: ContractSummary) => <StatusBadge status={c.status} /> },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Repository</h1>
          <p>All contracts in {activeOrg?.name}.</p>
        </div>
        <Link to="/studio/contract-ai/upload">
          <Button size="sm">Upload contract</Button>
        </Link>
      </div>

      {state.loading && <LoadingState />}
      {state.error && <CaiError error={state.error} onRetry={state.reload} />}
      {state.data && (
        <>
          <div className="toolbar">
            <div className="toolbar__search">
              <TextInput placeholder="Search contracts…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search contracts" />
            </div>
            <Select value={risk} onChange={(e) => setRisk(e.target.value)} aria-label="Filter by risk">
              <option value="">All risk</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="in_review">In review</option>
              <option value="expired">Expired</option>
            </Select>
          </div>
          {rows.length === 0 ? (
            <EmptyState icon="🗂️" title="No contracts found" description="Try adjusting filters, or upload your first contract." />
          ) : (
            <DataTable columns={cols} rows={rows} caption="Contracts" onRowClick={(c) => navigate(`/studio/contract-ai/contracts/${c.id}`)} />
          )}
        </>
      )}
    </>
  );
}
